import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { reportApi, reportKeys } from '../api';

/** Số dòng hiện; dài hơn thì nó là bảng dữ liệu, không phải biểu đồ. */
const VISIBLE = 12;

/**
 * Aging WIP — task đang dở đã nằm đó bao lâu (spec 16.2).
 *
 * Dạng thanh ngang chứ không phải chart thư viện: mỗi dòng cần **mã task, tên
 * người và số ngày** đọc được ngay, và một trục toạ độ không cho điều đó. Đây
 * cũng là biểu đồ mà người dùng muốn bấm vào từng dòng, nên nó vốn là một danh
 * sách có thanh, không phải một hình vẽ có nhãn.
 *
 * Ngưỡng "già" là p85 cycle time của **chính dự án này** — đội chậm hay nhanh thì
 * ngưỡng tự đi theo, và vượt nó nghĩa là bất thường so với lịch sử của chính đội.
 */
export function AgingWipList({
  projectId,
  asOf,
}: {
  projectId: string;
  asOf?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.agingWip(projectId, asOf ?? ''),
    queryFn: () => reportApi.agingWip(projectId, asOf),
  });

  const items = report.data?.items ?? [];
  const longest = Math.max(1, ...items.map((item) => item.ageDays));
  const threshold = report.data?.thresholdDays ?? null;

  return (
    <ChartFrame
      title={t('reports.agingTitle')}
      help="aging"
      subtitle={
        threshold === null
          ? t('reports.agingNoThreshold')
          : t('reports.agingThreshold', { days: threshold })
      }
      asOf={asOf ? new Date(asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && items.length === 0
          ? { title: t('reports.agingEmpty'), description: t('reports.agingEmptyHelp') }
          : null
      }
      legend={[
        { label: t('chart.category.in_progress'), color: FLOW_VAR.inProgress },
        { label: t('chart.category.todo'), color: FLOW_VAR.todo },
        { label: t('reports.overThreshold'), color: RAG_VAR.red },
      ]}
    >
      <ul className="flex flex-col gap-1">
        {items.slice(0, VISIBLE).map((item) => (
          <li key={item.taskId} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="w-20 shrink-0 font-mono">{item.taskKey}</span>
            <span className="min-w-32 flex-1 truncate" title={item.title}>
              {item.title}
            </span>
            <span className="w-24 shrink-0 truncate text-muted-foreground">
              {item.assigneeName ?? t('reports.unassigned')}
            </span>

            <span className="h-2 w-40 shrink-0 overflow-hidden rounded-full bg-border">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.round((item.ageDays / longest) * 100)}%`,
                  // Vượt ngưỡng thì đổi màu: màu ở đây là kênh thứ hai, còn
                  // kênh thứ nhất là con số ngày ngay bên cạnh.
                  backgroundColor: item.overThreshold
                    ? RAG_VAR.red
                    : item.statusCategory === 'in_progress'
                      ? FLOW_VAR.inProgress
                      : FLOW_VAR.todo,
                }}
              />
            </span>

            <span className="w-16 shrink-0 text-right">
              {t('reports.ageDays', { days: item.ageDays })}
            </span>
          </li>
        ))}

        {items.length > VISIBLE && (
          <li className="text-xs text-muted-foreground">
            {t('reports.agingMore', { count: items.length - VISIBLE })}
          </li>
        )}
      </ul>
    </ChartFrame>
  );
}
